import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../css/partner/ToolSidebar.css";
import { API_URL, authFetch, logout, NAME } from "./auth";
import { clientMeetingsAvailableForRole } from "./features";

export function navForRole(role, userId, isSuperuser) {
  const knowledgeItem = { key: "knowledge", label: "Knowledge Base", path: "/knowledge" };
  const shared = [
    { key: "inbox", label: "Inbox", path: "/inbox" },
    { key: "contacts", label: "Contacts", path: "/contacts" },
    ...(clientMeetingsAvailableForRole(role) ? [{ key: "meetings", label: "Meetings", path: "/meetings" }] : []),
    { key: "notifications", label: "Notifications", path: "/notifications" },
    { key: "profile", label: "Profile", path: "/profile" },
    { key: "settings", label: "Settings", path: "/settings" },
  ];

  if (isSuperuser) {
    return [
      { label: "Workspace", items: [
        { key: "dashboard", label: "Dashboard", path: "/dashboard" },
        { key: "project-requests", label: "Project Requests", path: "/project-requests" },
        { key: "projects", label: "Projects", path: "/projects" },
        { key: "team", label: "Team", path: "/team" },
        { key: "workboard", label: "Workboard", path: "/workboard" },
        { key: "approvals", label: "Approvals", path: "/approvals" },
        ...shared.slice(0, 4),
        knowledgeItem,
      ] },
      { label: "People", items: [
        { key: "job-applications", label: "Job Applications", path: "/job-applications" },
        { key: "offers-review", label: "Offer Reviews", path: "/offers/review" },
        { key: "employees", label: "Employees", path: "/employees" },
        { key: "clients", label: "Clients", path: "/clients" },
      ] },
      { label: "Account", items: shared.slice(4) },
    ];
  }

  const roleNav = {
    client: [{ key: "dashboard", label: "Dashboard", path: "/dashboard" }, { key: "projects", label: "Projects", path: "/projects" }, { key: "files", label: "Files", path: "/files" }, ...shared],
    partner: [{ key: "dashboard", label: "Dashboard", path: "/dashboard" }, { key: "leads", label: "Leads", path: "/leads" }, { key: "clients", label: "Clients", path: "/clients" }, { key: "commissions", label: "Commissions", path: "/commissions" }, { key: "client-form", label: "Client Form", path: `/new-project/${userId}` }, knowledgeItem, ...shared],
    hr: [{ key: "dashboard", label: "Dashboard", path: "/dashboard" }, { key: "project-requests", label: "Project Requests", path: "/project-requests" }, { key: "job-applications", label: "Job Applications", path: "/job-applications" }, { key: "offers-review", label: "Offer Reviews", path: "/offers/review" }, { key: "approvals", label: "Approvals", path: "/approvals" }, { key: "employees", label: "Employees", path: "/employees" }, knowledgeItem, ...shared],
    developer: [{ key: "dashboard", label: "Dashboard", path: "/dashboard" }, { key: "projects", label: "Assigned Projects", path: "/projects" }, { key: "workboard", label: "Workboard", path: "/workboard" }, { key: "files", label: "Files", path: "/files" }, knowledgeItem, ...shared],
    designer: [{ key: "dashboard", label: "Dashboard", path: "/dashboard" }, { key: "projects", label: "Assigned Projects", path: "/projects" }, { key: "workboard", label: "Workboard", path: "/workboard" }, { key: "files", label: "Files", path: "/files" }, knowledgeItem, ...shared],
    marketing: [{ key: "dashboard", label: "Dashboard", path: "/dashboard" }, { key: "projects", label: "Assigned Projects", path: "/projects" }, { key: "workboard", label: "Workboard", path: "/workboard" }, { key: "files", label: "Assets", path: "/files" }, knowledgeItem, ...shared],
    project_manager: [{ key: "dashboard", label: "Dashboard", path: "/dashboard" }, { key: "projects", label: "Projects", path: "/projects" }, { key: "team", label: "Team", path: "/team" }, { key: "workboard", label: "Workboard", path: "/workboard" }, { key: "approvals", label: "Approvals", path: "/approvals" }, { key: "offers-review", label: "Offer Reviews", path: "/offers/review" }, { key: "files", label: "Files", path: "/files" }, knowledgeItem, ...shared],
    staff: [{ key: "dashboard", label: "Dashboard", path: "/dashboard" }, { key: "projects", label: "Assigned Projects", path: "/projects" }, { key: "workboard", label: "Workboard", path: "/workboard" }, { key: "files", label: "Files", path: "/files" }, knowledgeItem, ...shared],
  };

  return [{ label: "Portal", items: roleNav[role] || roleNav.client }];
}

export function roleLabel(role, isSuperuser) {
  if (isSuperuser) return "Superuser";
  const labels = {
    client: "Client",
    partner: "Partner",
    hr: "HR",
    developer: "Developer",
    designer: "Designer",
    marketing: "Marketing",
    project_manager: "Project Manager",
    staff: "Staff",
  };
  return labels[role] || "Portal";
}

function SidebarNav({ sections, active, onNavigate, unreadCount = 0 }) {
  return (
    <nav className="tool-nav">
      {sections.map((section) => (
        <div className="tool-nav-section" key={section.label}>
          <span>{section.label}</span>
          {section.items.map((item) => (
            <button key={item.key} type="button" className={active === item.key ? "active" : ""} onClick={() => onNavigate(item.path)}>
              <span className="tool-nav-label">{item.label}</span>
              {item.key === "notifications" && unreadCount > 0 ? <strong className="tool-nav-badge">{unreadCount > 99 ? "99+" : unreadCount}</strong> : null}
            </button>
          ))}
        </div>
      ))}
    </nav>
  );
}

function ToolSidebar({ active = "dashboard" }) {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeButtonRef = useRef(null);
  const userId = localStorage.getItem("user_id");
  const isSuperuser = localStorage.getItem("is_superuser") === "true";
  const role = localStorage.getItem("portal_role") || "client";
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const sections = navForRole(role, userId, isSuperuser);
  const label = roleLabel(role, isSuperuser);

  useEffect(() => {
    let activeRequest = true;
    async function loadUnreadCount() {
      try {
        const isInternal = localStorage.getItem("is_internal") === "true";
        const isPartner = localStorage.getItem("is_partner") === "true";
        const internalRole = isSuperuser || isInternal || ["hr", "project_manager", "developer", "designer", "marketing", "staff", "superuser"].includes(role);
        const prefix = internalRole ? "notifications" : isPartner ? "partner/notifications" : "client/notifications";
        const response = await authFetch(`${API_URL}/${prefix}/recent/`);
        if (!response.ok) return;
        const data = await response.json();
        const items = Array.isArray(data) ? data : data.results || [];
        const unreadCount = typeof data.unread_count === "number" ? data.unread_count : items.filter((item) => !item.read && !item.is_read).length;
        if (activeRequest) setNotificationUnreadCount(unreadCount);
      } catch (err) {
        if (activeRequest) setNotificationUnreadCount(0);
      }
    }
    loadUnreadCount();
    return () => {
      activeRequest = false;
    };
  }, [role, isSuperuser]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    closeButtonRef.current?.focus();
    document.addEventListener("keydown", onKeyDown);
    document.body.classList.add("portal-drawer-open");
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("portal-drawer-open");
    };
  }, [drawerOpen]);

  const goTo = (path) => {
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <>
      <header className="portal-mobile-header">
        <div>
          <strong>{NAME}</strong>
          <span>{label}</span>
        </div>
        <button type="button" aria-label="Open navigation" aria-expanded={drawerOpen} aria-controls="portal-mobile-drawer" onClick={() => setDrawerOpen(true)}>
          <span aria-hidden="true">☰</span>
        </button>
      </header>

      <aside className="tool-sidebar" aria-label="Portal navigation">
        <div className="tool-top">
          <div>
            <span>{NAME}</span>
            <h3>{label}</h3>
            <p>Active</p>
          </div>
        </div>

        {active !== "dashboard" && (
          <button type="button" className="tool-back" onClick={() => navigate(-1)}>Back</button>
        )}

        <SidebarNav sections={sections} active={active} onNavigate={goTo} unreadCount={notificationUnreadCount} />

        <div className="tool-session">
          <button type="button" className="tool-logout" onClick={() => logout(navigate)}>Logout</button>
        </div>
      </aside>

      {drawerOpen ? (
        <>
          <button type="button" className="portal-drawer-backdrop" aria-label="Close navigation" onClick={() => setDrawerOpen(false)} />
          <aside id="portal-mobile-drawer" className="portal-mobile-drawer open" aria-label="Mobile portal navigation">
            <div className="tool-top mobile-drawer-top">
              <div>
                <span>{NAME}</span>
                <h3>{label}</h3>
                <p>Active</p>
              </div>
              <button type="button" aria-label="Close navigation" ref={closeButtonRef} onClick={() => setDrawerOpen(false)}>×</button>
            </div>
            {active !== "dashboard" && (
              <button type="button" className="tool-back" onClick={() => { setDrawerOpen(false); navigate(-1); }}>Back</button>
            )}
            <SidebarNav sections={sections} active={active} onNavigate={goTo} unreadCount={notificationUnreadCount} />
            <div className="tool-session">
              <button type="button" className="tool-logout" onClick={() => logout(navigate)}>Logout</button>
            </div>
          </aside>
        </>
      ) : null}
    </>
  );
}

export default ToolSidebar;
